package org.matthewo.api

import kotlin.jvm.JvmName
import kotlinx.serialization.Serializable
import kotlin.math.roundToInt
import org.matthewo.symbolprocessor.Period.*
import org.matthewo.schema.MetricsMeta
import org.matthewo.schema.games.*
import org.matthewo.math.plus
import org.matthewo.math.convert

@Serializable
data class MetricsBioBuzzAPI<N : Number>(
    override val total: N,
    override val npTotal: N,
    override val auto: N,
    override val teleop: N,
    override val endgame: N,
    override val penalty: N,
    override val minorFoul: N,
    override val majorFoul: N,
    override val apTip: N,
    override val apLeave: N,
    override val apPark: N,
    override val tpTip: N,
    override val tpHive: N,
    override val tpGarden: N,
    override val epFlowerBottom: N,
    override val epFlowerOwned: N,
    override val epPark: N,
    override val rpBonus: N,
    override val rpSwarm: N,
    override val rpPollinator1: N,
    override val rpPollinator2: N,
    val details: BioBuzzScoreDetailsAPI? = null,
) : MetricsAPI<N>, MetricsBioBuzz<N> {
    constructor(team1: MetricsBioBuzz<N>, team2: MetricsBioBuzz<N>) : this(
        total = team1.total + team2.total,
        npTotal = team1.npTotal + team2.npTotal,
        auto = team1.auto + team2.auto,
        teleop = team1.teleop + team2.teleop,
        endgame = team1.endgame + team2.endgame,
        penalty = team1.penalty + team2.penalty,
        minorFoul = team1.minorFoul + team2.minorFoul,
        majorFoul = team1.majorFoul + team2.majorFoul,
        apTip = team1.apTip + team2.apTip,
        apLeave = team1.apLeave + team2.apLeave,
        apPark = team1.apPark + team2.apPark,
        tpTip = team1.tpTip + team2.tpTip,
        tpHive = team1.tpHive + team2.tpHive,
        tpGarden = team1.tpGarden + team2.tpGarden,
        epFlowerBottom = team1.epFlowerBottom + team2.epFlowerBottom,
        epFlowerOwned = team1.epFlowerOwned + team2.epFlowerOwned,
        epPark = team1.epPark + team2.epPark,
        rpBonus = team1.rpBonus + team2.rpBonus,
        rpSwarm = team1.rpSwarm + team2.rpSwarm,
        rpPollinator1 = team1.rpPollinator1 + team2.rpPollinator1,
        rpPollinator2 = team1.rpPollinator2 + team2.rpPollinator2,
    )
    
    companion object {
        inline operator fun <reified N : Number> invoke(team1: MetricsBioBuzz<*>, team2: MetricsBioBuzz<*>) = MetricsBioBuzzAPI(
            total = team1.total.convert<N>() + team2.total.convert<N>(),
            npTotal = team1.npTotal.convert<N>() + team2.npTotal.convert<N>(),
            auto = team1.auto.convert<N>() + team2.auto.convert<N>(),
            teleop = team1.teleop.convert<N>() + team2.teleop.convert<N>(),
            endgame = team1.endgame.convert<N>() + team2.endgame.convert<N>(),
            penalty = team1.penalty.convert<N>() + team2.penalty.convert<N>(),
            minorFoul = team1.minorFoul.convert<N>() + team2.minorFoul.convert<N>(),
            majorFoul = team1.majorFoul.convert<N>() + team2.majorFoul.convert<N>(),
            apTip = team1.apTip.convert<N>() + team2.apTip.convert<N>(),
            apLeave = team1.apLeave.convert<N>() + team2.apLeave.convert<N>(),
            apPark = team1.apPark.convert<N>() + team2.apPark.convert<N>(),
            tpTip = team1.tpTip.convert<N>() + team2.tpTip.convert<N>(),
            tpHive = team1.tpHive.convert<N>() + team2.tpHive.convert<N>(),
            tpGarden = team1.tpGarden.convert<N>() + team2.tpGarden.convert<N>(),
            epFlowerBottom = team1.epFlowerBottom.convert<N>() + team2.epFlowerBottom.convert<N>(),
            epFlowerOwned = team1.epFlowerOwned.convert<N>() + team2.epFlowerOwned.convert<N>(),
            epPark = team1.epPark.convert<N>() + team2.epPark.convert<N>(),
            rpBonus = team1.rpBonus.convert<N>() + team2.rpBonus.convert<N>(),
            rpSwarm = team1.rpSwarm.convert<N>() + team2.rpSwarm.convert<N>(),
            rpPollinator1 = team1.rpPollinator1.convert<N>() + team2.rpPollinator1.convert<N>(),
            rpPollinator2 = team1.rpPollinator2.convert<N>() + team2.rpPollinator2.convert<N>(),
        )
    }
}

inline fun <reified N : Number> MetricsBioBuzz<*>.convert() = serialize { convert<N>() }

@Serializable
data class BioBuzzScoreDetailsAPI(
    override val esRedFlower: BioBuzzScoreDetails.Flower,
    override val esBlueFlower: BioBuzzScoreDetails.Flower,
    override val esScoringFlower: BioBuzzScoreDetails.Flower,
    override val esAudienceFlower: BioBuzzScoreDetails.Flower,
) : BioBuzzScoreDetails

fun BioBuzzScoreDetails.serializeDetails() = BioBuzzScoreDetailsAPI(
    esRedFlower,
    esBlueFlower,
    esScoringFlower,
    esAudienceFlower,
)

fun <N : Number> MetricsBioBuzz<N>.serialize() = MetricsBioBuzzAPI(
    total = total,
    npTotal = npTotal,
    auto = auto,
    teleop = teleop,
    endgame = endgame,
    penalty = penalty,
    minorFoul = minorFoul,
    majorFoul = majorFoul,
    apTip = apTip,
    apLeave = apLeave,
    apPark = apPark,
    tpTip = tpTip,
    tpHive = tpHive,
    tpGarden = tpGarden,
    epFlowerBottom = epFlowerBottom,
    epFlowerOwned = epFlowerOwned,
    epPark = epPark,
    rpBonus = rpBonus,
    rpSwarm = rpSwarm,
    rpPollinator1 = rpPollinator1,
    rpPollinator2 = rpPollinator2,
    details = if (this is BioBuzzScoreDetails) serializeDetails() else null,
)

@JvmName("serializeGeneric")
fun <T : Number, R : Number> MetricsBioBuzz<T>.serialize(serializer: T.() -> R) = MetricsBioBuzzAPI(
    total = total.serializer(),
    npTotal = npTotal.serializer(),
    auto = auto.serializer(),
    teleop = teleop.serializer(),
    endgame = endgame.serializer(),
    penalty = penalty.serializer(),
    minorFoul = minorFoul.serializer(),
    majorFoul = majorFoul.serializer(),
    apTip = apTip.serializer(),
    apLeave = apLeave.serializer(),
    apPark = apPark.serializer(),
    tpTip = tpTip.serializer(),
    tpHive = tpHive.serializer(),
    tpGarden = tpGarden.serializer(),
    epFlowerBottom = epFlowerBottom.serializer(),
    epFlowerOwned = epFlowerOwned.serializer(),
    epPark = epPark.serializer(),
    rpBonus = rpBonus.serializer(),
    rpSwarm = rpSwarm.serializer(),
    rpPollinator1 = rpPollinator1.serializer(),
    rpPollinator2 = rpPollinator2.serializer(),
    details = if (this is BioBuzzScoreDetails) serializeDetails() else null,
)

@JvmName("serializeDouble")
fun MetricsBioBuzz<Double>.serialize() = serialize { roundToInt().toShort() }

@JvmName("serializeNumber")
fun MetricsBioBuzz<Number>.serialize() = serialize { toShort() }

fun MetricsBioBuzz<*>.stringify() = """
        total: $total
        npTotal: $npTotal
        auto: $auto
        teleop: $teleop
        endgame: $endgame
        penalty: $penalty
        minorFoul: $minorFoul
        majorFoul: $majorFoul
        apTip: $apTip
        apLeave: $apLeave
        apPark: $apPark
        tpTip: $tpTip
        tpHive: $tpHive
        tpGarden: $tpGarden
        epFlowerBottom: $epFlowerBottom
        epFlowerOwned: $epFlowerOwned
        epPark: $epPark
        rpBonus: $rpBonus
        rpSwarm: $rpSwarm
        rpPollinator1: $rpPollinator1
        rpPollinator2: $rpPollinator2
    """.trimIndent()

object MetricsBioBuzzMeta : MetricsMeta<MetricsBioBuzz<*>> {
    override val names = linkedMapOf(
        MetricsBioBuzz<*>::total to "Total",
        MetricsBioBuzz<*>::npTotal to "NP Total",
        MetricsBioBuzz<*>::auto to "Auto",
        MetricsBioBuzz<*>::teleop to "Teleop",
        MetricsBioBuzz<*>::endgame to "Endgame",
        MetricsBioBuzz<*>::penalty to "Foul",
        MetricsBioBuzz<*>::apTip to "Tip",
        MetricsBioBuzz<*>::apLeave to "Leave",
        MetricsBioBuzz<*>::apPark to "Park",
        MetricsBioBuzz<*>::tpTip to "Tip",
        MetricsBioBuzz<*>::tpHive to "Hive",
        MetricsBioBuzz<*>::tpGarden to "Garden",
        MetricsBioBuzz<*>::epFlowerBottom to "Flower Bottom",
        MetricsBioBuzz<*>::epFlowerOwned to "Flower Owned",
        MetricsBioBuzz<*>::epPark to "Park",
        MetricsBioBuzz<*>::rpBonus to "Bonus RP",
        MetricsBioBuzz<*>::rpSwarm to "Swarm RP",
        MetricsBioBuzz<*>::rpPollinator1 to "Pollinator 1 RP",
        MetricsBioBuzz<*>::rpPollinator2 to "Pollinator 2 RP",
    )
    
    override val periods = linkedMapOf(
        MetricsBioBuzz<*>::total to ALL,
        MetricsBioBuzz<*>::npTotal to ALL,
        MetricsBioBuzz<*>::auto to AUTO,
        MetricsBioBuzz<*>::teleop to TELEOP,
        MetricsBioBuzz<*>::endgame to ENDGAME,
        MetricsBioBuzz<*>::penalty to ALL,
        MetricsBioBuzz<*>::apTip to AUTO,
        MetricsBioBuzz<*>::apLeave to AUTO,
        MetricsBioBuzz<*>::apPark to AUTO,
        MetricsBioBuzz<*>::tpTip to TELEOP,
        MetricsBioBuzz<*>::tpHive to TELEOP,
        MetricsBioBuzz<*>::tpGarden to TELEOP,
        MetricsBioBuzz<*>::epFlowerBottom to ENDGAME,
        MetricsBioBuzz<*>::epFlowerOwned to ENDGAME,
        MetricsBioBuzz<*>::epPark to ENDGAME,
        MetricsBioBuzz<*>::rpBonus to ALL,
        MetricsBioBuzz<*>::rpSwarm to ALL,
        MetricsBioBuzz<*>::rpPollinator1 to ALL,
        MetricsBioBuzz<*>::rpPollinator2 to ALL,
    )
}
